<template>
  <div v-for="(flight, index) in displayedFlights" :key="index" class="problem-flight-v2__result"
    :class="{ 'is-dragging': draggedIndex === index }" draggable="true"
    @dragstart="dragStart($event, flight?.FlightCode)" @dragover.prevent @drop="drop($event)">
    <div class="problem-flight-v2__result__airline">
      <span class="problem-flight-v2__result__airline__svg">
        <img :src="flight.logoPath" :alt="flight?.AirlineCode" class="problem-flight-v2__result__airline__svg__img" />
      </span>
      <span class="problem-flight-v2__result__code">
        {{ flight?.AirlineName }}  {{ flight?.FlightCode }}
      </span>
    </div>
    <hr class="problem-flight-v2__result__seperator">
    <div class="problem-flight-v2__result__details">
      <div class="problem-flight-v2__result__details__time">
        <FlightIcon :class="'calendar-icon'" symbolId="calendar" /> 
        <span class="problem-flight-v2__result__details__text"> {{ formatFlightDateTime(flight?.DepartureScheduledDateTime) }} </span>
      </div>
      <div class="problem-flight-v2__result__details__route">
        <span class="problem-flight-v2__result__details__route__takeOff">
          <FlightIcon :class="'takeoff-icon'" symbolId="airplane-take-off" /> 
          <span class="problem-flight-v2__result__details__text"> {{ removeAirportWord(flight?.DepartureAirport) }}</span>
        </span>
        <span class="problem-flight-v2__result__details__route__landing">
          <FlightIcon :class="'landing-icon'" symbolId="airplane-landing" /> 
          <span class="problem-flight-v2__result__details__text"> {{  removeAirportWord(flight?.ArrivalAirport) }} </span>
        </span>
      </div>
      <div class="problem-flight-v2__result__details__button">
        <a class="link-button primary link-button-color"  :href="`/${fields?.recent_flight_button_link}?Action=StartProblemFlight&ProblemFlightId=${flight.ProblemFlightId}`">{{ fields?.recent_flight_button_text }}</a>
      </div>
    </div>
  </div>
  <div v-if="showMore || displayedFlights.length < 3" class="problem-flight-v2__result">
    <div class="problem-flight-v2__result__extra-row">
      <span class="problem-flight-v2__result__extra-row__text">{{ displayedFlights.length < 1 ? fields?.not_found?.no_records_message :  fields?.not_found?.some_records_message}}</span>
      <div class="problem-flight-v2__result__details__button"> <a class="link-button primary link-button-color" :href="fields?.not_found?.button_link">{{ fields?.not_found?.button_text }}</a></div>
    </div>
  </div>
  <div class="problem-flight-v2__result__readmore" @click="toggleFlightsView" v-if="showToggleButton">
    <span class="problem-flight-v2__result__readmore__text">
      {{ showMore ? fields?.show_less_text : fields?.show_more_text }}
    </span>
    <span class="problem-flight-v2__result__readmore__icon" :class="{ transform: showMore }">
      <FlightIcon :class="'chevron-bottom'" symbolId="chevron-bottom" />
    </span>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import Item, { InitialData } from "../../types/types";
import { cultureCheck, removeAirportWord } from "../../utils/utils";
import FlightIcon from "../FlightIcon/FlightIcon.vue";
import dayjs from 'dayjs';
import 'dayjs/locale/nl';
import 'dayjs/locale/de';
import 'dayjs/locale/en';

const props = defineProps<{
  flights: Item[];
  fields: InitialData | null;
}>();

const flightDate = (datetime: string) => {
  return dayjs(datetime).locale(cultureCheck()).format('DD MMMM YYYY');
};

const flightDateTimeAt = () => {
  switch(cultureCheck()) {
    default:
    case 'nl':
      return ' om ';
    case 'de':
      return ' um ';
    case 'en':
      return ' at ';
  }
};

const flightTime = (datetime: string) => {
  return dayjs(datetime).format('HH:mm');
};

const formatFlightDateTime = (datetime: string) => {
  return flightDate(datetime) + flightDateTimeAt() + flightTime(datetime);
};


const draggedIndex = ref<number | null>(null);
const showMore = ref(false);
const draggedFlight = ref<string | null>(null);

const displayedFlights = computed(() => {
  return showMore.value ? props.flights : props.flights.slice(0, 3);
});

const showToggleButton = computed(() => {
  return props.flights.length > 3;
});

const toggleFlightsView = () => {
  showMore.value = !showMore.value;
};

const dragStart = (event: DragEvent, flightCode: string) => {
  draggedFlight.value = flightCode;
};

const drop = (event: DragEvent) => {
  if (!draggedFlight.value) return;

  const draggedIndex = props.flights.findIndex(f => f.FlightCode === draggedFlight.value);
  const targetIndex = props.flights.findIndex(f => f.FlightCode === (event.target as HTMLElement).closest('.problem-flight-v2__result')?.getAttribute('data-key'));

  if (draggedIndex === -1 || targetIndex === -1) return;

  const draggedFlightItem = props.flights.splice(draggedIndex, 1)[0];
  props.flights.splice(targetIndex, 0, draggedFlightItem);
  draggedFlight.value = null;
};

</script>
